/* eslint-disable @typescript-eslint/no-unsafe-call */
import { Button, Grid } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import * as ol from "ol";
import { Feature } from "ol";
import GeometryType from "ol/geom/GeometryType";
import Point from "ol/geom/Point";
import Polygon from "ol/geom/Polygon";
import { Draw, Modify, Snap } from "ol/interaction";
import VectorLayer from "ol/layer/Vector";
import { Vector } from "ol/source";
import React, { useCallback, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Coordinate,
  PortServiceCreateDto,
  PortServiceItemDto,
  PortServiceTypeEnum,
  PortServiceUpdateDto,
  usePortApi,
  usePortServiceApi,
} from "~/api/GeneratedApi";
import GenericStepperDialog, {
  DialogStep,
} from "~/components/GenericStepperDialog";
import LoadingBox from "~/components/LoadingBox";
import { addPortServicesToMap } from "~/pages/MapPage/functions/addPortServicesToMap";
import { addPortsToMap } from "~/pages/MapPage/functions/addPortsToMap";
import { createPortServiceLayer } from "~/pages/MapPage/functions/createPortServiceLayer";
import { findLayerByTitle } from "~/pages/MapPage/functions/findLayerByTitle";
import { findSourceByTitle } from "~/pages/MapPage/functions/findSourceByTitle";
import { mapToReadOnly } from "~/pages/MapPage/functions/mapToReadOnly";
import useCreateMap from "~/pages/MapPage/hooks/useCreateMap";
import { LayerTitle } from "~/pages/MapPage/types/layers";
import { useCustomMutation } from "~/utils/hooks/useCustomMutation";
import { useCustomQuery } from "~/utils/hooks/useCustomQuery";
import BerthPortSelectForm from "../components/BerthPortSelectForm";
import { ServiceForm, ServiceFormInputs } from "./ServiceForm";

export type DialogAction = "add" | "edit" | "view";

type Props = {
  activeAction?: DialogAction;
  serviceId?: number;
  open?: boolean;
  portId?: number;
  onClose: () => void;
  onSuccess?: () => void;
};

function formDataToArgs(
  x: ServiceFormInputs,
  point: Coordinate,
): PortServiceCreateDto | PortServiceUpdateDto {
  return {
    description: x?.description ?? "",
    name: x?.name ?? "",
    location: point ?? undefined,
    ...({ portId: x?.portId } ?? {}),
    availabilityInfo: x?.availabilityInfo ?? "",
    type: x?.portServiceType ?? PortServiceTypeEnum.Other,
  };
}

const useStyles = makeStyles((theme) => ({
  mapContainer: {
    height: "90%",
    width: "100%",
    marginBottom: theme.spacing(1),
  },
  drawActionButton: {
    display: "block",
    margin: "0 auto",
    color: theme.palette.warning.contrastText,
    backgroundColor: theme.palette.warning.main,
    "&:hover": {
      backgroundColor: theme.palette.warning.dark,
    },
  },
}));

export const ServiceDialog = ({
  activeAction,
  onClose,
  portId,
  serviceId,
  onSuccess,
  open = true,
}: Props): JSX.Element | null => {
  const { t } = useTranslation("port-services");
  const actions: DialogAction[] = ["add", "edit", "view"];
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<ol.Map>();
  const drawRef = useRef<Draw>();
  const sourceRef = useRef<Vector>();
  const [drawDone, setDrawDone] = useState(false);
  const classes = useStyles();
  const formId = `${activeAction}-service-form`;
  const { createMap } = useCreateMap();
  const nextRef = useRef<() => void>();
  const [selectedPortId, setSelectedPortId] = React.useState<
    number | undefined
  >(portId);
  const [coordinates, setCoordinates] = useState<Coordinate>();

  const [formData, setFormData] = React.useState<
    PortServiceCreateDto | PortServiceUpdateDto | PortServiceItemDto
  >();

  const portApi = usePortApi();
  const serviceApi = usePortServiceApi();

  const isEditing = activeAction === "edit";
  const isCreating = activeAction === "add";
  const isViewing = activeAction === "view";

  const { data: service, isLoading: isLoadingService } = useCustomQuery(
    { key: "port-service", id: serviceId },
    () => serviceApi.getPortService(serviceId!),
    { enabled: !!serviceId },
  );

  const { data: services } = useCustomQuery(
    { key: "services-for-port", id: selectedPortId },
    () => serviceApi.getPortServices({ id: selectedPortId! }),
    { enabled: !!selectedPortId },
  );

  const { mutate: addServiceMutation } = useCustomMutation(
    (createdService: PortServiceCreateDto) =>
      serviceApi.addPortService(createdService),
    {
      onSuccess: () => {
        onSuccess?.();
        onClose();
      },
    },
  );

  const { mutate: editServiceMutation } = useCustomMutation(
    (editedService: PortServiceUpdateDto) =>
      serviceApi.editPortService(editedService, serviceId!),
    {
      onSuccess: () => {
        onSuccess?.();
        onClose();
      },
    },
  );

  React.useEffect(() => {
    if (!service) return;
    setFormData(service);
    if (service.location) setCoordinates(service.location);
  }, [service]);

  const { data: port } = useCustomQuery(
    { key: "port", id: selectedPortId },
    () => portApi.getPort(selectedPortId!),
    { enabled: !!selectedPortId },
  );

  const handleClearDrawing = useCallback(() => {
    if (mapRef.current) {
      const source = findSourceByTitle(
        mapRef.current,
        LayerTitle.PortServicesDraw,
      );
      source?.clear();
      setCoordinates(undefined);
      if (isEditing) {
        drawRef.current = new Draw({
          source: sourceRef.current,
          type: GeometryType.POINT,
        });
        drawRef.current?.on("drawend", () => {
          setDrawDone(true);
          if (drawRef.current) {
            mapRef.current?.removeInteraction(drawRef.current);
          }
        });
      }
      if ((isEditing || isCreating) && drawRef.current) {
        mapRef.current.addInteraction(drawRef.current);
      }
    }
  }, [isCreating, isEditing]);

  const steps: DialogStep[] = useMemo(() => {
    if (isLoadingService) {
      return [
        {
          title: t("general:loading"),
          content: <LoadingBox />,
        },
      ];
    }

    let title = "";
    switch (activeAction) {
      case "add":
        title = t("fill-info");
        break;
      case "edit":
        title = t("edit-info");
        break;
      case "view":
        title = service?.name ?? "";
        break;
      default:
        break;
    }

    const lastStep = {
      title: isViewing ? title : t("general:confirm"),
      content: (
        <Grid container>
          <Grid
            item
            xs={4}
            ref={mapContainerRef}
            className={classes.mapContainer}
          />

          <Grid item xs={8}>
            <ServiceForm
              small
              onSubmit={() => null}
              formId={formId}
              defaultValues={formData as PortServiceItemDto}
              readOnly
            />
          </Grid>
        </Grid>
      ),
    };

    if (isViewing) return [lastStep];

    return [
      {
        title: t("click-service-point"),
        content: (
          <>
            <div ref={mapContainerRef} className={classes.mapContainer} />
            {isCreating && (
              <BerthPortSelectForm
                fullWidth
                portId={portId}
                onSubmit={(id) => {
                  setSelectedPortId(id);
                }}
              />
            )}
            <Button
              variant="contained"
              classes={{ root: classes.drawActionButton }}
              onClick={handleClearDrawing}
            >
              {t("general:clear")}
            </Button>
          </>
        ),
      },
      {
        title,
        content: (
          <ServiceForm
            portId={selectedPortId}
            onSubmit={(d) => {
              setFormData(formDataToArgs(d, coordinates!));
              nextRef.current?.();
            }}
            formId={formId}
            defaultValues={service}
            readOnly={isViewing}
          />
        ),
      },
      lastStep,
    ];
  }, [
    isLoadingService,
    activeAction,
    isViewing,
    t,
    classes.mapContainer,
    classes.drawActionButton,
    formId,
    formData,
    isCreating,
    portId,
    handleClearDrawing,
    selectedPortId,
    service,
    coordinates,
  ]);

  const handleStepLogic = useCallback(
    (step: number) => {
      // Add map on step 0 and the required interactions
      if (step !== 0 && step !== 2) {
        mapRef.current?.dispose();
        mapRef.current = undefined;
        drawRef.current = undefined;
      }

      if (step === 0 && mapContainerRef.current) {
        if (!mapRef.current) {
          mapRef.current = createMap({
            target: mapContainerRef.current,
            includedLayers: [
              LayerTitle.OSM,
              LayerTitle.PortServices,
              LayerTitle.Ports,
            ],
            popup: false,
            existingMap: mapRef.current,
          });
          // Add a layer for drawing the service
          const { layer: drawLayer, source: drawSource } =
            createPortServiceLayer(true);

          // Add a layer for all other services
          const { layer: serviceLayer } = createPortServiceLayer();

          sourceRef.current = drawSource;
          mapRef.current.addLayer(drawLayer);
          mapRef.current.addLayer(serviceLayer);

          // Set the existing services layer as visible, ignoring the actual settings
          findLayerByTitle(mapRef.current, LayerTitle.PortServices)?.setVisible(
            true,
          );
          // Add the drawing interaction when editing/creating
          if (!isViewing && !coordinates) {
            drawRef.current = new Draw({
              source: sourceRef.current,
              type: GeometryType.POINT,
            });
            mapRef.current.addInteraction(drawRef.current);
          }

          // Add the point of the currently edited service
          if (coordinates) {
            const point = new Point([coordinates.x, coordinates.y]);
            const feature = new Feature({
              geometry: point,
            });

            const ext = feature.getGeometry()?.getExtent();
            if (feature && ext && mapRef.current) {
              sourceRef.current?.addFeature(feature);
              setDrawDone(true);
              mapRef.current.getView().fit(ext);
              mapRef.current.getView().setZoom(17);
              if (drawRef.current) {
                mapRef.current?.removeInteraction(drawRef.current);
              }
            }
          }

          // Add the modify / snap interactions when editing/creating
          if (!isViewing) {
            const snap = new Snap({ source: sourceRef.current });
            mapRef.current.addInteraction(snap);
            const modify = new Modify({ source: sourceRef.current });
            mapRef.current.addInteraction(modify);
            drawRef.current?.on("drawend", () => {
              setDrawDone(true);
              if (drawRef.current) {
                mapRef.current?.removeInteraction(drawRef.current);
              }
            });
          }
        }
        if (mapRef.current) {
          // Add/remove the drawing interaction depending on if we have a port selected or not
          if (isCreating && drawRef.current) {
            if (selectedPortId === undefined) {
              mapRef.current?.removeInteraction(drawRef.current);
            } else if (
              !mapRef.current
                .getInteractions()
                .getArray()
                .includes(drawRef.current)
            ) {
              mapRef.current.addInteraction(drawRef.current);
            }
          }

          // Add current port's services
          addPortServicesToMap(mapRef.current, services?.results ?? []);

          // Add the port we are adding this to
          const portLayer = findLayerByTitle(mapRef.current, LayerTitle.Ports);
          portLayer?.setVisible(true);
          addPortsToMap(mapRef.current, [...(port ? [port] : [])]);

          if (port) {
            const portFeature = (portLayer as VectorLayer)
              ?.getSource()
              ?.getFeatureById(`port${selectedPortId}`);
            const mapView = mapRef.current?.getView();
            const poly = portFeature.getGeometry() as Polygon;
            const ext = poly.getExtent();
            mapView?.fit(ext);
            const curZoom = mapView?.getZoom();
            if (!curZoom) return;
            mapView?.setZoom(curZoom - 0.2);
          }
        }
      }
      if (step === 2 && mapContainerRef.current && !mapRef.current) {
        mapRef.current = createMap({
          target: mapContainerRef.current,
          includedLayers: [LayerTitle.OSM, LayerTitle.PortServices],
          popup: false,
          existingMap: mapRef.current,
        });

        // Add a layer for the drawing
        const { layer: drawLayer, source } = createPortServiceLayer(true);
        mapRef.current.addLayer(drawLayer);

        // Set the existing layer as visible, ignoring the actual settings
        findLayerByTitle(mapRef.current, LayerTitle.PortServices)?.setVisible(
          true,
        );
        if (coordinates) {
          const point = new Point([coordinates.x, coordinates.y]);
          const feature = new Feature({
            geometry: point,
            name: formData?.name,
          });
          feature.setId("finished");
          source.addFeature(feature);

          const ext = point.getExtent();
          mapRef.current.getView().fit(ext);
          mapRef.current.getView().setZoom(17);
        }
        mapToReadOnly(mapRef.current);
      }
    },
    [
      createMap,
      isViewing,
      coordinates,
      isCreating,
      services?.results,
      port,
      selectedPortId,
      formData?.name,
    ],
  );

  const onDrawDone = useCallback(() => {
    const c = (
      sourceRef.current?.getFeatures()[0].getGeometry() as Point
    ).getCoordinates();
    if (!c) return;
    setCoordinates({ x: c[0], y: c[1] });
  }, []);

  const renderNextButton = useCallback(
    (step, next) => {
      if (isViewing) return null;

      switch (step) {
        case 0:
          return (
            <Button
              variant="contained"
              onClick={(e) => {
                onDrawDone();
                next();
                e.preventDefault();
              }}
              color="secondary"
              disabled={!drawDone}
            >
              {t("general:next")}
            </Button>
          );

        case 1:
          return (
            <Button
              type="submit"
              form={formId}
              variant="contained"
              color="secondary"
            >
              {t("general:next")}
            </Button>
          );
        case 2:
          return (
            <Button
              variant="contained"
              color="secondary"
              onClick={() => {
                if (isCreating) {
                  addServiceMutation(formData as PortServiceCreateDto);
                } else if (isEditing) {
                  editServiceMutation(formData as PortServiceUpdateDto);
                }
              }}
            >
              {t("general:save")}
            </Button>
          );
        default:
          return null;
      }
    },
    [
      isViewing,
      drawDone,
      t,
      formId,
      onDrawDone,
      isCreating,
      isEditing,
      addServiceMutation,
      formData,
      editServiceMutation,
    ],
  );

  const renderBackButton = useCallback(
    (step, back, handleDialogClose) => {
      return (
        <Button
          onClick={() => {
            if (step === 0) {
              handleDialogClose();
              onClose();
            } else {
              back();
            }
          }}
        >
          {isViewing
            ? t("general:close")
            : t(`general:${step === 0 ? "cancel" : "back"}`)}
        </Button>
      );
    },
    [isViewing, onClose, t],
  );

  if (!activeAction || !actions.includes(activeAction)) return null;

  return (
    <GenericStepperDialog
      open={open}
      steps={steps}
      onClose={onClose}
      dialogTitle=""
      minHeight="40%"
      height="95%"
      contentHeight="80%"
      onStepActive={handleStepLogic}
      renderNextButton={renderNextButton}
      renderBackButton={renderBackButton}
      nextRef={nextRef}
      hideSteps={isViewing || isLoadingService}
    />
  );
};
